import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Shield, Car, ArrowRight, AlertTriangle, Heart, Umbrella, ExternalLink, Star, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Insurance = () => {
  const location = useLocation();
  const car = location.state?.car;

  useEffect(() => {
    document.title = 'Car Insurance Quotes South Africa | Compare & Save | AutoSearch SA';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Compare car insurance quotes from Discovery, Outsurance, MiWay, King Price & more. Get comprehensive or third-party cover. Free quotes in minutes.');
  }, []);

  const [quoteForm, setQuoteForm] = useState({
    vehicleValue: car?.price || 300000,
    vehicleAge: car?.year ? new Date().getFullYear() - car.year : 3,
    coverType: 'comprehensive'
  });

  // Insurance estimate calculation - realistic SA rates
  const calculateInsurance = () => {
    const baseRate = quoteForm.coverType === 'comprehensive' ? 0.042 : 0.018;
    const ageMultiplier = quoteForm.vehicleAge < 2 ? 1.15 : 
                          quoteForm.vehicleAge < 4 ? 1.05 : 
                          quoteForm.vehicleAge < 7 ? 1.0 : 
                          quoteForm.vehicleAge < 10 ? 0.95 : 0.85;
    const valueMultiplier = quoteForm.vehicleValue > 800000 ? 1.1 :
                            quoteForm.vehicleValue > 500000 ? 1.05 :
                            quoteForm.vehicleValue > 300000 ? 1.0 : 0.95;
    const annualPremium = quoteForm.vehicleValue * baseRate * ageMultiplier * valueMultiplier;
    const monthlyPremium = annualPremium / 12;
    const baseExcess = quoteForm.coverType === 'comprehensive' ? 
      Math.max(5000, Math.round(quoteForm.vehicleValue * 0.015)) : 
      Math.max(3000, Math.round(quoteForm.vehicleValue * 0.01));
    return {
      monthly: Math.round(monthlyPremium),
      annual: Math.round(annualPremium),
      excess: Math.round(baseExcess / 500) * 500
    };
  };

  const insurance = calculateInsurance();

  const trackReferralClick = async (provider, url) => {
    try {
      await axios.post(`${API}/track/click`, null, {
        params: {
          action_type: 'insurance_referral',
          referral_url: url,
          source: provider
        }
      });
    } catch (e) {
      // Don't block navigation on tracking failure
    }
  };

  const handleProviderClick = (provider, url) => {
    trackReferralClick(provider, url);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleHippoClick = () => {
    trackReferralClick('Hippo.co.za', 'https://www.hippo.co.za/car-insurance/');
    window.open('https://www.hippo.co.za/car-insurance/', '_blank', 'noopener,noreferrer');
  };

  const coverTypes = [
    {
      id: 'comprehensive',
      name: 'Comprehensive',
      icon: Shield,
      desc: 'Full cover including theft, accidents, fire, and third-party damage',
      features: ['Theft & Hijacking', 'Accident damage', 'Fire damage', 'Third-party liability', 'Windscreen cover', 'Roadside assistance']
    },
    {
      id: 'thirdparty',
      name: 'Third-Party',
      icon: Car,
      desc: 'Covers damage you cause to other vehicles and property',
      features: ['Third-party vehicle damage', 'Third-party property damage', 'Legal liability', 'Personal accident cover']
    }
  ];

  const providers = [
    { name: 'Discovery Insure', rating: 4.5, desc: 'Rewards safe driving with cashback', url: 'https://www.discovery.co.za/car-insurance' },
    { name: 'King Price', rating: 4.4, desc: 'Premiums that decrease monthly', url: 'https://www.kingprice.co.za' },
    { name: 'Outsurance', rating: 4.3, desc: 'OUTbonus for claim-free years', url: 'https://www.outsurance.co.za' },
    { name: 'MiWay', rating: 4.2, desc: 'Flexible cover, easy online quotes', url: 'https://www.miway.co.za' },
    { name: 'First for Women', rating: 4.3, desc: 'Tailored cover for women drivers', url: 'https://www.firstforwomen.co.za' },
    { name: 'Budget Insurance', rating: 4.1, desc: 'Affordable cover options', url: 'https://www.budgetinsurance.co.za' }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="insurance-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Car Insurance Quotes</h1>
            <p className="text-xl text-purple-100 mb-6">
              Get an estimate, then compare real quotes from South Africa's top insurers.
            </p>
            <button
              onClick={handleHippoClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 rounded-lg font-semibold hover:bg-purple-50 transition"
              data-testid="hippo-hero-btn"
            >
              Compare Quotes on Hippo
              <ExternalLink className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quote Calculator */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get an Estimate</h2>

              {car && (
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-purple-600 font-medium">Getting quote for:</p>
                  <p className="text-lg font-semibold text-gray-900">{car.title}</p>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Value</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={quoteForm.vehicleValue}
                      onChange={(e) => setQuoteForm({ ...quoteForm, vehicleValue: Number(e.target.value) })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg"
                      data-testid="vehicle-value-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Age</label>
                  <select
                    value={quoteForm.vehicleAge}
                    onChange={(e) => setQuoteForm({ ...quoteForm, vehicleAge: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                    data-testid="vehicle-age-select"
                  >
                    <option value={0}>Brand New (0-1 year)</option>
                    <option value={2}>1-3 years</option>
                    <option value={4}>3-5 years</option>
                    <option value={6}>5-7 years</option>
                    <option value={8}>7+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Cover Type</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {coverTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setQuoteForm({ ...quoteForm, coverType: type.id })}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition ${
                          quoteForm.coverType === type.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        data-testid={`cover-type-${type.id}`}
                      >
                        <type.icon className={`w-8 h-8 mb-2 ${quoteForm.coverType === type.id ? 'text-purple-600' : 'text-gray-400'}`} />
                        <h3 className="font-semibold text-gray-900">{type.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{type.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Estimate Results */}
              <div className="bg-gray-50 rounded-xl p-6 mt-8" data-testid="estimate-results">
                <h3 className="font-semibold text-gray-900 mb-4">Estimated Premium</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Monthly</p>
                    <p className="text-2xl font-bold text-purple-600" data-testid="estimate-monthly">R{insurance.monthly.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Annual</p>
                    <p className="text-xl font-semibold text-gray-900">R{insurance.annual.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Excess</p>
                    <p className="text-xl font-semibold text-gray-900">R{insurance.excess.toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 text-center">
                  * This is a rough estimate. Get accurate quotes by comparing insurers below.
                </p>

                {/* CTA after estimate */}
                <div className="mt-4 text-center">
                  <button
                    onClick={handleHippoClick}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                    data-testid="hippo-estimate-btn"
                  >
                    Get Accurate Quotes on Hippo
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Insurance Providers */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Or Get a Quote Directly</h2>
              <p className="text-gray-500 text-sm mb-6">Visit any insurer directly to get a personalised quote.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {providers.map((provider, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleProviderClick(provider.name, provider.url)}
                    className="flex items-start gap-4 border border-gray-200 rounded-xl p-4 text-left hover:border-purple-300 hover:bg-purple-50 transition group"
                    data-testid={`provider-${provider.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition">{provider.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{provider.desc}</p>
                      <div className="flex items-center gap-1 mt-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < Math.floor(provider.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">{provider.rating}/5</span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-500 mt-1 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>

            {/* Why Insure */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mt-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Don't Skip Insurance!</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Car theft in SA costs victims an average of R180,000</li>
                    <li>Third-party claims can exceed R1 million</li>
                    <li>Comprehensive cover gives you peace of mind</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Hippo Compare CTA */}
            <div className="bg-white rounded-2xl shadow-sm p-6 border-2 border-purple-200" data-testid="hippo-sidebar">
              <div className="text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Compare All Insurers</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Use Hippo to compare quotes from all major SA insurers in one place. One form, multiple quotes.
                </p>
                <div className="space-y-3 text-left mb-6">
                  {['One form, multiple quotes', 'Free & no obligation', 'Takes under 5 minutes', 'All major SA insurers'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleHippoClick}
                  className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition flex items-center justify-center gap-2"
                  data-testid="hippo-sidebar-btn"
                >
                  Compare on Hippo
                  <ExternalLink className="w-4 h-4" />
                </button>
                <p className="text-xs text-gray-400 mt-3">You'll be redirected to hippo.co.za</p>
              </div>
            </div>

            {/* Also Protect Yourself */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Also Protect Yourself</h3>
              <p className="text-xs text-gray-500 mb-4">Your car can be replaced. Your income can't.</p>
              <div className="space-y-3">
                <a href="/life-cover" className="flex items-center gap-3 p-3 rounded-lg bg-red-50 hover:bg-red-100 transition">
                  <Heart className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Life Cover</p>
                    <p className="text-xs text-gray-500">Protect your family - get a free quote</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                </a>
                <a href="/life-cover" className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition">
                  <Umbrella className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Income Protection</p>
                    <p className="text-xs text-gray-500">Safeguard your earnings</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insurance;
